/**
 * Recommendation Service
 *
 * Handles all recommendation-related API operations including:
 * - Generating recommendations from quiz results
 * - Retrieving recommendation details
 * - Saving recommendations for users
 */

import { baseClient } from './base-client';
import type {
  ApiResponse,
  GenerateRecommendationsRequest,
  GenerateRecommendationsResponse,
  RecommendationDetails,
  SaveRecommendationRequest,
  SaveRecommendationResponse,
} from './types';

class RecommendationService {
  private readonly basePath = '/recommendations';

  /**
   * Generate product recommendations based on quiz session
   */
  async generateRecommendations(
    request: GenerateRecommendationsRequest
  ): Promise<ApiResponse<GenerateRecommendationsResponse>> {
    return baseClient.post<GenerateRecommendationsResponse>(
      this.basePath,
      request
    );
  }

  /**
   * Get detailed information about a specific recommendation
   */
  async getRecommendationDetails(recommendationId: string): Promise<ApiResponse<RecommendationDetails>> {
    return baseClient.get<RecommendationDetails>(`${this.basePath}/${recommendationId}`);
  }

  /**
   * Save a recommendation for the user
   */
  async saveRecommendation(
    request: SaveRecommendationRequest
  ): Promise<ApiResponse<SaveRecommendationResponse>> {
    return baseClient.post<SaveRecommendationResponse>(`${this.basePath}/save`, request);
  }

  /**
   * Get saved recommendations for the current user
   */
  async getSavedRecommendations(): Promise<ApiResponse<RecommendationDetails[]>> {
    return baseClient.get<RecommendationDetails[]>(`${this.basePath}/saved`);
  }
}

/**
 * Export singleton instance
 */
export const recommendationService = new RecommendationService();
